// src/components/SignInModal.tsx
//
// The sign-in dialog — design "Pre-login Home", artboard 1c, with the
// mobile sheet from 1b.
//
// Two shapes, one set of content:
//
// · ≥ sm — a 760px two-column dialog. Sand panel on the left listing what
//   an account unlocks, form on the right. The panel is the argument; the
//   right column is the single button that acts on it.
// · < sm — a bottom sheet. Thumb-reachable, and the benefits collapse to a
//   sand box between the header and the button.
//
// The blocks are ordered with `order-*` rather than duplicated, because the
// two layouts interleave: mobile wants header → benefits → button, desktop
// wants benefits beside header → button.
//
// Composed from the Radix primitives rather than `ui/dialog`'s DialogContent
// because that wrapper hard-codes a centred, zooming panel — fighting its
// transform and slide-from-top animation to produce a bottom sheet costs
// more than assembling the three parts here.

import React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import {
  Dialog,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { BrandMark } from "@/components/BrandMark";
import { useAuth } from "@/hooks/useAuth";
import { bandStats, getLanding, landingKey } from "@/api/service/landing";
import { cn } from "@/lib/utils";

interface SignInModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Overrides the default list for a page that can name something more
   *  specific — the practice builder sells saved filters, not streaks. */
  features?: string[];
}

const DEFAULT_FEATURES = [
  "Mock tests on the real GATE clock",
  "Progress and weak topics across subjects",
  "Step-by-step solutions with reasoning",
  "Bookmarks and every mistake you make",
];

export const SignInModal: React.FC<SignInModalProps> = ({
  isOpen,
  onClose,
  features = DEFAULT_FEATURES,
}) => {
  const { signInWithGoogle } = useAuth();

  // Shared cache with the landing page, so opening this from home costs
  // nothing. Only fetched once the dialog is actually opened.
  const { data } = useQuery({
    queryKey: landingKey,
    queryFn: getLanding,
    enabled: isOpen,
    staleTime: 10 * 60 * 1000,
  });

  // The dialog has room for three figures, not four — "Beta / Early access"
  // is a landing-page flourish and would crowd the panel here.
  const stats = bandStats(data?.data?.stats).slice(0, 3);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/60 data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />

        <DialogPrimitive.Content
          className={cn(
            "fixed z-50 flex flex-col overflow-hidden bg-card shadow-lg duration-200",
            // < sm — bottom sheet
            "inset-x-0 bottom-0 max-h-[92vh] overflow-y-auto rounded-t-2xl",
            "data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
            // ≥ sm — centred two-column dialog
            "sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:w-[calc(100vw-3rem)] sm:max-w-[760px]",
            "sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-2xl sm:border sm:border-border",
            "sm:grid sm:grid-cols-[280px_1fr] sm:grid-rows-[auto_1fr]",
            "sm:data-[state=closed]:slide-out-to-bottom-0 sm:data-[state=open]:slide-in-from-bottom-0",
            "sm:data-[state=closed]:zoom-out-95 sm:data-[state=open]:zoom-in-95",
          )}
        >
          <DialogPrimitive.Close className="absolute right-3.5 top-3.5 z-10 flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>

          {/* --- header -------------------------------------------------- */}
          <div className="order-1 flex items-start gap-3.5 px-5 pt-6 sm:order-none sm:col-start-2 sm:row-start-1 sm:flex-col sm:gap-0 sm:px-10 sm:pt-11">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-primary text-primary-foreground">
              <BrandMark size={22} className="text-primary-foreground" />
            </span>
            <div className="flex min-w-0 flex-col gap-1 pr-8 sm:mt-5 sm:gap-2.5 sm:pr-0">
              <DialogTitle className="text-[20px] font-semibold tracking-[-0.024em] sm:text-[26px] sm:leading-tight">
                Sign in to gatetard
              </DialogTitle>
            </div>
          </div>

          {/* --- what you unlock ----------------------------------------- */}
          <div className="order-2 mx-5 mt-5 flex flex-col rounded-xl border border-sand-border bg-sand p-4 sm:order-none sm:col-start-1 sm:row-span-2 sm:row-start-1 sm:m-0 sm:rounded-none sm:border-0 sm:border-r sm:border-sand-border sm:p-7">
            <span className="eyebrow hidden text-sand-muted sm:block">
              What you unlock
            </span>

            <ul className="flex flex-col gap-2.5 sm:mt-5 sm:gap-4">
              {features.map((feature) => (
                <li key={feature} className="flex items-start gap-2.5 sm:gap-3">
                  <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-amber" />
                  <span className="text-[13.5px] leading-snug text-sand-foreground sm:text-[14.5px] sm:leading-relaxed">
                    {feature}
                  </span>
                </li>
              ))}
            </ul>

            {/* The sheet has no room for these, and on a phone the button is
                the only thing that matters. */}
            <div className="mt-auto hidden gap-5 pt-7 sm:flex">
              {stats.map((s) => (
                <div key={s.label} className="flex flex-col gap-1">
                  <span className="text-[18px] font-semibold tracking-[-0.02em]">
                    {s.value}
                  </span>
                  <span className="font-mono text-[9.5px] uppercase tracking-[0.1em] text-sand-muted">
                    {s.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* --- the one action ------------------------------------------ */}
          <div className="order-3 flex flex-col px-5 pb-6 pt-4 sm:order-none sm:col-start-2 sm:row-start-2 sm:px-10 sm:pb-9 sm:pt-7">
            {/* `my-auto` rather than a top-aligned block: the left panel sets
                the dialog's height, so the leftover space in this column is
                split above and below the button instead of collecting into
                one hole under it. */}
            <div className="sm:my-auto">
              <button
                onClick={signInWithGoogle}
                className="flex w-full items-center justify-center gap-2.5 rounded-[10px] bg-primary px-5 py-3.5 text-[15px] font-semibold text-primary-foreground transition-opacity hover:opacity-90 sm:py-4 sm:text-[15.5px]"
              >
                <GoogleG />
                Continue with Google
              </button>

              <p className="mt-3 text-center text-[12.5px] leading-relaxed text-muted-foreground sm:text-left">
                Secure sign-in via Google. We never see your password.
              </p>
            </div>

            {/* The "FREE — the syllabus stays open" line is gone: everything
                is free during beta, so singling one thing out as free implied
                the rest was not. The way out stays — on mobile that is the
                close button in the corner. */}
            <div className="hidden border-t border-border pt-6 sm:block">
              <button
                onClick={onClose}
                className="whitespace-nowrap text-[13px] font-semibold text-muted-foreground transition-colors hover:text-foreground"
              >
                Keep browsing
              </button>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </Dialog>
  );
};

const GoogleG = () => (
  <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M23.5 12.27c0-.79-.07-1.54-.2-2.27H12v4.51h6.47a5.54 5.54 0 0 1-2.4 3.63v3h3.88c2.26-2.09 3.55-5.17 3.55-8.87Z"
    />
    <path
      fill="#34A853"
      d="M12 24c3.24 0 5.96-1.08 7.95-2.91l-3.88-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.28v3.09A12 12 0 0 0 12 24Z"
    />
    <path
      fill="#FBBC05"
      d="M5.27 14.29a7.2 7.2 0 0 1 0-4.58V6.62H1.28a12 12 0 0 0 0 10.76l3.99-3.09Z"
    />
    <path
      fill="#EA4335"
      d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.44-3.44C17.95 1.19 15.24 0 12 0A12 12 0 0 0 1.28 6.62l3.99 3.09C6.22 6.86 8.87 4.75 12 4.75Z"
    />
  </svg>
);

export default SignInModal;
